// Pure classifier for ApiError → UI action class per DESIGN.md §10.
//
// Split out of ApiErrorDispatcher so it can be exercised without a React
// tree and so the dispatcher component file exports only components.
//
// The DESIGN.md §10 table maps backend (status, error-code) pairs to UI
// actions. This module is the single source of truth for that mapping:
// callers that don't want the default toast behavior (e.g. /admin/grants
// row-greying for role_not_held) can classify the error themselves and
// take a different branch while keeping the taxonomy consistent.

import { ApiError } from "./client";

/**
 * Classification of an ApiError into a DESIGN.md §10 row.
 *
 * Discriminated on `kind`. Additional fields carry the details needed by
 * the dispatcher (attempted role, offending location, etc.) — these come
 * straight out of the backend body and are not invented locally.
 */
export type ErrorClass =
  | { kind: "session_expired"; requestId: string | undefined }
  | { kind: "role_required"; requestId: string | undefined }
  | {
      kind: "invalid_role";
      attemptedRole: string | undefined;
      requestId: string | undefined;
    }
  | {
      kind: "role_not_held";
      locationId: string | undefined;
      role: string | undefined;
      requestId: string | undefined;
    }
  | { kind: "role_insufficient"; requestId: string | undefined }
  | { kind: "provisioning_pending"; requestId: string | undefined }
  | { kind: "partial_success"; requestId: string | undefined }
  | {
      kind: "unknown";
      status: number;
      code: string;
      message: string;
      requestId: string | undefined;
    };

/**
 * Pull the most specific error code out of an ApiError.
 *
 * Precedence:
 *   1. `err.code` — already normalized by the client wrapper from
 *      `body.error`.
 *   2. `body.error` — redundant safety net for future code paths that
 *      bypass the wrapper.
 *   3. `status_<N>` — fallback so callers always have a string to match.
 *
 * Returns a string, never undefined.
 */
export function extractErrorCode(err: ApiError): string {
  if (err.code !== undefined && err.code !== "") return err.code;
  const body = err.body;
  if (
    typeof body === "object" &&
    body !== null &&
    "error" in body &&
    typeof (body as { error: unknown }).error === "string"
  ) {
    return (body as { error: string }).error;
  }
  return `status_${err.status}`;
}

/**
 * Safe field reader for the error body. Returns undefined if body isn't
 * an object or field is missing / wrong type.
 */
function readBodyString(err: ApiError, field: string): string | undefined {
  const body = err.body;
  if (typeof body !== "object" || body === null) return undefined;
  if (!(field in body)) return undefined;
  const value = (body as Record<string, unknown>)[field];
  return typeof value === "string" ? value : undefined;
}

/**
 * Classify an ApiError into one of the DESIGN.md §10 UI action rows.
 *
 * Side-effect free. Callers decide what to do with the classification —
 * the default dispatcher (see ApiErrorDispatcher.tsx) does toasts +
 * redirects; pages with inline grey-out can branch earlier.
 */
export function classifyApiError(err: ApiError): ErrorClass {
  const code = extractErrorCode(err);
  const requestId = err.requestId;

  // 401 — session missing/expired. The api client already redirects to
  // /signin; we classify it so callers that opted out of the redirect
  // (SessionProvider bootstrap, etc.) can still pattern-match.
  if (err.status === 401) {
    return { kind: "session_expired", requestId };
  }

  if (err.status === 400) {
    if (code === "role_required") {
      return { kind: "role_required", requestId };
    }
    if (code === "invalid_role") {
      return {
        kind: "invalid_role",
        attemptedRole: readBodyString(err, "attempted_role"),
        requestId,
      };
    }
  }

  if (err.status === 403) {
    if (code === "role_not_held_at_location") {
      return {
        kind: "role_not_held",
        locationId: readBodyString(err, "location_id"),
        role: readBodyString(err, "role"),
        requestId,
      };
    }
    if (code === "role_insufficient_for_action") {
      return { kind: "role_insufficient", requestId };
    }
    if (code === "provisioning_pending") {
      return { kind: "provisioning_pending", requestId };
    }
  }

  // SCIM partial-success — placeholder for task 46.4 row-level rendering.
  if (err.status === 207) {
    return { kind: "partial_success", requestId };
  }

  return {
    kind: "unknown",
    status: err.status,
    code,
    message: err.message,
    requestId,
  };
}
